import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import type { Incident } from '@models/incidents';
import { INCIDENT_STATUS_LABELS, getIncidentStatusTone } from '@features/incidents/constants';
import { formatRigaDateTime } from '@utils/date';

import LoadingAnimation from '@components/LoadingAnimation';
import StatusChip from '@components/ui/StatusChip';
import s from './IncidentsTable.module.css';

const COL_WIDTHS = ['42%', '12%', '12%', '16%', '14%', '4%'];

type IncidentsTableProps = {
  isLoading: boolean;
  isFetching: boolean;
  rows: Incident[];
  onRowClick: (row: Incident) => void;
};

function IncidentsTable({ isLoading, isFetching, rows, onRowClick }: IncidentsTableProps) {
  const showOverlay = isFetching && !isLoading && rows.length > 0;

  return (
    <div className={s.tableShell}>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <colgroup>
            {COL_WIDTHS.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th>Incident</th>
              <th>Status</th>
              <th className={s.alignRight}>Customers</th>
              <th>Created by</th>
              <th>Created</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={COL_WIDTHS.length} className={s.loadingCell}>
                  <LoadingAnimation compact />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COL_WIDTHS.length} className={s.empty}>
                  No incidents match your search.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className={s.row} onClick={() => onRowClick(row)}>
                  <td className={s.nameCell}>
                    <div className={s.name}>{row.name}</div>
                    {row.description && <div className={s.description}>{row.description}</div>}
                  </td>
                  <td>
                    <StatusChip
                      label={INCIDENT_STATUS_LABELS[row.status] ?? row.status}
                      tone={getIncidentStatusTone(row.status)}
                    />
                  </td>
                  <td className={s.alignRight}>
                    <span className={s.count}>{row.involvedCustomerCount.toLocaleString()}</span>
                  </td>
                  <td className={s.muted}>{row.createdBy?.name ?? '—'}</td>
                  <td className={s.muted}>{formatRigaDateTime(row.createdAt)}</td>
                  <td className={s.chevronCell}>
                    <ChevronRightIcon sx={{ fontSize: 18 }} aria-hidden />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showOverlay && (
        <div className={s.fetchingOverlay}>
          <LoadingAnimation compact />
        </div>
      )}
    </div>
  );
}

export default IncidentsTable;
